import type { BrandType } from '@mathflat/domain/@entities/Brand/type'

import { landingPathApi } from '@/api/landingPath.api'
import type { Academy } from '@/dto'
import type { StrapiDataResponse } from '@/types/common/api'
import dateUtils from '@/utils/DateUtils'

import type Controller from './Controller'

type BrandConfig = {
  linkKey: keyof Pick<
    res.HcmsControlLandingPath,
    'homeButtonLinkMathflat'
  >
}

const BRAND_CONFIG: Record<BrandType, BrandConfig> = {
  MATHFLAT: { linkKey: 'homeButtonLinkMathflat' },
}

class HcmsLandingPathController implements Controller {
  adapter = {}

  getHomeButtonLandingPath = async (brand: BrandType, dCode: Academy['academyId']) => {
    const { data: hcmsControlLandingPathList } = await landingPathApi.getHomeButtonLandingPath()
    const activatedLandingPath = this._findActivatedLandingPath(hcmsControlLandingPathList, brand)

    const defaultTarget = this._extractUrlFromLandingPath(activatedLandingPath, brand)
    const customTarget = this._findCustomUrlFromLandingPath(defaultTarget?.customUrls, dCode)

    return customTarget?.customUrl || defaultTarget?.url || null
  }

  private _findActivatedLandingPath(
    hcmsControlLandingPathList: StrapiDataResponse<res.HcmsControlLandingPath>[],
    brand: BrandType,
  ) {
    const now = new Date()
    const config = BRAND_CONFIG[brand]

    return hcmsControlLandingPathList.find((landingPath) => {
      const linkData = landingPath.attributes[config.linkKey]
      if (!linkData) return false

      return dateUtils.checkIsWithinInterval(now, {
        startDate: new Date(linkData.startDatetime),
        endDate: new Date(linkData.endDatetime),
        strictCheck: true,
      })
    })
  }

  private _extractUrlFromLandingPath(
    hcmsControlLandingPath: StrapiDataResponse<res.HcmsControlLandingPath> | undefined,
    brand: BrandType,
  ): res.HcmsControlLandingPath['homeButtonLinkMathflat'] {
    if (!hcmsControlLandingPath) return null

    const config = BRAND_CONFIG[brand]
    return hcmsControlLandingPath.attributes[config.linkKey] ?? null
  }

  private _findCustomUrlFromLandingPath(
    customUrls: res.ButtonLink['customUrls'],
    currentDCode: Academy['academyId'],
  ) {
    return customUrls?.find((customUrl) =>
      customUrl.Dcode.some((dcode) => dcode.children.some((child) => child.text === currentDCode)),
    )
  }
}

export const hcmsLandingPathController = new HcmsLandingPathController()
